/**
 * LIVE PREVIEW — the real app, in an iframe, instead of a drawing of it.
 *
 * The drawn phone re-implements every screen from a block manifest, and it drifted: three
 * radios where the app has four plus a medication row, diet lists flattened into pipe
 * strings, four named detoxes collapsed into one meaningless "14-day detox". Dustin,
 * 2026-09-03: *"unless I see it on the app, I don't know what's there."*
 *
 * So when a state says where it lives (`state.preview`), we load the app's own web build
 * from the tracker instead. A drawing can be wrong; the screen itself cannot.
 *
 * DRAFT WORDING. The iframe boots showing PUBLISHED copy. It posts `bb-preview-ready` when
 * it is live, and we answer with everything currently in the editor — including edits not
 * yet published, which is the whole point: without it this is a viewer, and he would still
 * be publishing blind.
 *
 * 🔴 ORIGIN IS CHECKED BOTH WAYS. We only accept `ready` from the frame's own origin, and
 * we post drafts only to that origin — never `"*"`, which would hand this session's
 * unpublished wording to whatever happened to be in the frame.
 */
import React from "react";

export type PreviewTarget = { path: string; params?: Record<string, string> };

/**
 * What the frame says about a click, beyond the candidate keys.
 *  - `walked`: a press has already been let through to the app, so the frame may be on a page
 *    or have a sheet open that the loaded state didn't — "it was clicked, so it's on the screen
 *    I'm editing" no longer holds.
 *  - `path`: the app's own pathname when clicked (includes the Worker's `/preview` mount).
 * Both are absent from a frame built before the two-click change; treat that as not walked.
 */
export type PickInfo = { walked: boolean; path: string | null };

/** Where the app's web build is served from — the tracker Worker's /preview mount. */
export function previewBase(trackerUrl: string | null | undefined): string | null {
  if (!trackerUrl) return null;
  return trackerUrl.replace(/\/+$/, "") + "/preview";
}

/**
 * Build the iframe URL.
 *
 * `base` arrives as `<origin>/preview|<token>` — the signature is minted server-side (the
 * browser never sees the shared secret) and has to ride in the query string, because the
 * alternative is a cookie and a third-party cookie in an iframe is exactly what browsers
 * drop. See the tracker's preview-gate.ts.
 */
export function previewUrl(base: string, target: PreviewTarget): string {
  const [root, token] = base.split("|");
  const path = target.path === "/" ? "" : target.path;
  const q = new URLSearchParams({ pv: "1", ...(target.params ?? {}) });
  if (token) q.set("t", token);
  return `${root}${path}?${q.toString()}`;
}

/**
 * The viewports the app can be laid out for — real iPhones' points, never a size invented to
 * fit the pane. The 6.7″ size exists because the app pins its Today card to the foot of the
 * screen: on the taller phone there is more room above it, so you can check how a long card
 * (Program complete) reads on the biggest screen a customer has, not just the standard one.
 */
export const DEVICES = {
  standard: { w: 390, h: 844, label: "iPhone · 6.1″" },
  large: { w: 430, h: 932, label: "iPhone Plus · 6.7″" },
} as const;
export type DeviceId = keyof typeof DEVICES;

export function LiveFrame({
  src,
  drafts,
  scale,
  onPick,
  selected = null,
  device = "standard",
}: {
  src: string;
  /** Every key the editor currently holds, published or not. */
  drafts: Record<string, string>;
  /** Shrink factor for the picture only — the app still lays out at 390×844. */
  scale: number;
  /**
   * They clicked a line in the real app. Every key that could have produced those words —
   * "Back" is four different keys — for the editor to narrow against the screen it is on.
   */
  onPick?: (keys: string[], info: PickInfo) => void;
  /**
   * The field selected on the right. Sent to the frame so a second click only presses the line
   * that is STILL selected — pick another field and the next click on the phone selects again.
   */
  selected?: string | null;
  /** Which real phone to lay the app out for. */
  device?: DeviceId;
}) {
  const PHONE_W = DEVICES[device].w;
  const PHONE_H = DEVICES[device].h;
  const ref = React.useRef<HTMLIFrameElement | null>(null);
  const [ready, setReady] = React.useState(false);
  // Held in a ref so the message listener does not have to be torn down and rebuilt every
  // time the editor re-renders — which is on every keystroke.
  const pick = React.useRef(onPick);
  React.useEffect(() => { pick.current = onPick; }, [onPick]);
  const origin = React.useMemo(() => {
    try { return new URL(src).origin; } catch { return null; }
  }, [src]);

  /**
   * "Click again to open" — shown beside a control the frame is holding a press for. The frame
   * owns the timing (it disarms after `armMs`, on a click elsewhere, or when the press goes
   * through) and tells us with `bb-preview-disarm`; the local timer is only a backstop.
   */
  const [hint, setHint] = React.useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const hintTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearHint = React.useCallback(() => {
    if (hintTimer.current) clearTimeout(hintTimer.current);
    hintTimer.current = null;
    setHint(null);
  }, []);
  React.useEffect(() => clearHint, [clearHint]);

  // A fresh src is a fresh document, so the old frame's readiness — and its armed click — mean nothing.
  React.useEffect(() => { setReady(false); clearHint(); }, [src, clearHint]);

  React.useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      if (!origin || e.origin !== origin) return;
      const d = e.data as any;
      if (d?.type === "bb-preview-ready") setReady(true);
      // Click-to-edit. The frame reports which keys could have produced the words that were
      // clicked; narrowing to one is the editor's job, since only it knows which screen is
      // open. Keys are strings from our own app, but they come off a postMessage, so filter
      // to strings rather than trusting the shape.
      if (d?.type === "bb-preview-pick" && Array.isArray(d.keys)) {
        const keys = d.keys.filter((k: unknown): k is string => typeof k === "string").slice(0, 12);
        const info: PickInfo = { walked: d.walked === true, path: typeof d.path === "string" ? d.path : null };
        const r = d.rect;
        const num = (v: unknown) => typeof v === "number" && Number.isFinite(v);
        clearHint();
        if (d.actionable === true && r && num(r.x) && num(r.y) && num(r.w) && num(r.h)) {
          setHint({ x: r.x, y: r.y, w: r.w, h: r.h });
          const ms = num(d.armMs) ? Math.min(10000, Math.max(500, d.armMs)) : 4000;
          hintTimer.current = setTimeout(() => setHint(null), ms);
        }
        if (keys.length) pick.current?.(keys, info);
      }
      if (d?.type === "bb-preview-disarm") clearHint();
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [origin, clearHint]);

  // Tell the frame which field is selected, so an armed second click is dropped once he moves on.
  React.useEffect(() => {
    if (!ready || !origin) return;
    ref.current?.contentWindow?.postMessage({ type: "bb-preview-select", key: selected ?? null }, origin);
  }, [ready, selected, origin]);

  // Push drafts on ready, and again whenever he types. The app replaces its whole draft set
  // each time, so a key he reverts stops showing its edit rather than sticking.
  React.useEffect(() => {
    if (!ready || !origin) return;
    ref.current?.contentWindow?.postMessage({ type: "bb-preview-copy", copy: drafts }, origin);
  }, [ready, drafts, origin]);

  /**
   * 🔴 THE IFRAME IS ALWAYS 390×844 AND IS SCALED WITH A TRANSFORM — it is never simply
   * given smaller dimensions.
   *
   * A smaller iframe is a smaller VIEWPORT, and the app is responsive: it would faithfully
   * lay itself out for a 304pt-wide phone that does not exist, so type looked oversized
   * against the content and long screens ran off the bottom. Dustin, 2026-09-05: *"certain
   * screens are giant. Some of them run clear off the page… less representative of the
   * actual app."* Exactly right, and it made the preview lie in the one way it is supposed
   * to be incapable of.
   *
   * Laying out at a real phone's points and then shrinking the picture is also what the
   * drawn preview has always done (`Phone`'s `scale`), so the two now agree.
   */
  return (
    <div
      style={{
        position: "relative",
        width: Math.round(PHONE_W * scale),
        height: Math.round(PHONE_H * scale),
        borderRadius: 30,
        overflow: "hidden",
        background: "#fff",
        boxShadow: "0 18px 50px rgba(0,0,0,0.45)",
      }}
    >
      <iframe
        ref={ref}
        src={src}
        title="Live app preview"
        width={PHONE_W}
        height={PHONE_H}
        style={{ transform: `scale(${scale})`, transformOrigin: "top left" }}
        // 🔴 NO `sandbox` ATTRIBUTE, deliberately. `sandbox="allow-scripts"` without
        // `allow-same-origin` gives the frame an OPAQUE origin, and an opaque origin has no
        // localStorage — which is precisely what the preview seeds its session and protocol
        // state into. It would have rendered a permanently signed-out app and looked like a
        // bug in the screen. The frame is cross-origin to this page already, so the
        // same-origin policy isolates it without any help from us.
      />
      {hint && (() => {
        // In the frame's own points, scaled with the picture. Below the control unless that
        // would run off the phone, then above it.
        const W = Math.round(PHONE_W * scale), H = Math.round(PHONE_H * scale);
        const pillW = 132, pillH = 24;
        const left = Math.max(6, Math.min(W - pillW - 6, (hint.x + hint.w / 2) * scale - pillW / 2));
        const below = (hint.y + hint.h) * scale + 6;
        const top = below + pillH + 6 > H ? Math.max(6, hint.y * scale - pillH - 6) : below;
        return (
          <div role="status" style={{
            position: "absolute", left, top, width: pillW, height: pillH, boxSizing: "border-box",
            display: "grid", placeItems: "center", pointerEvents: "none", zIndex: 2,
            borderRadius: 999, background: "rgba(20,16,40,0.88)", color: "#fff",
            boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
            fontFamily: "Figtree, sans-serif", fontWeight: 700, fontSize: 11.5, whiteSpace: "nowrap",
          }}>
            Click again to open
          </div>
        );
      })()}
      {!ready && (
        <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", background: "#F3EEE3", fontFamily: "Figtree, sans-serif", fontSize: 12.5, color: "#6E665A" }}>
          Loading the app…
        </div>
      )}
    </div>
  );
}
